/**
 * The hosts and headers a repository credential can use, as both the save's
 * validation (`environment-validation.ts`) and the secrets setup the card
 * builds (`secrets-setup-items.ts`) need them. No imports: the card runs in
 * the browser.
 */

export const CREDENTIAL_HOSTS_MAX_COUNT = 10;

const HOST_NAME_PATTERN =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const HEADER_NAME_PATTERN = /^[\w!#$%&'*+.^`|~-]{1,64}$/;
// Hop-by-hop and framing headers, which a credential would break, and Host,
// which the firewall sets itself.
const FORBIDDEN_HEADER_NAMES = new Set([
  'host',
  'connection',
  'content-length',
  'keep-alive',
  'te',
  'trailer',
  'transfer-encoding',
  'upgrade',
]);
const HEADER_PREFIX_PATTERN = /^[\w.-]{0,32}$/;

/**
 * A DNS name, never an address. The URL standard reads a name whose last label
 * is a number, as in `10.0.0.1` or `0x7f.1`, as an IPv4 address; credentials
 * are for named services such as registries, and the firewall refuses private
 * addresses anyway.
 */
export function isCredentialHostName(host: string): boolean {
  if (!HOST_NAME_PATTERN.test(host)) {
    return false;
  }
  try {
    return !/^\d+\.\d+\.\d+\.\d+$/.test(new URL(`http://${host}`).hostname);
  } catch {
    return false;
  }
}

/** Whether a header can carry a credential without replacing HTTP framing. */
export function canCarryCredential(headerName: string): boolean {
  return (
    HEADER_NAME_PATTERN.test(headerName) && !FORBIDDEN_HEADER_NAMES.has(headerName.toLowerCase())
  );
}

/** Whether a credential header prefix is one word. */
export function isCredentialHeaderPrefix(prefix: string): boolean {
  return HEADER_PREFIX_PATTERN.test(prefix);
}
